// @ts-nocheck
import React, { useState } from 'react';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { URL } from '../../service/Service';
import {
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

const newPasswordEndpoint = '/api/admin/newPassword';

const Password = ({ onCancel }) => {
  const jwtToken = sessionStorage.getItem('jwt');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formValues, setFormValues] = useState({
    oldPassword: '',
    newPassword: '',
    repeatNewPassword: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [messageErr, setMessageErr] = useState('');
  const [subtitleErr, setSubtitleErr] = useState('');

  const validateForm = () => {
    const errors = {};

    if (!formValues.oldPassword) {
      errors.oldPassword = 'Introduza a palavra-passe atual.';
    }

    if (!formValues.newPassword) {
      errors.newPassword = 'Introduza a nova palavra-passe.';
    } else if (formValues.newPassword.length < 8) {
      errors.newPassword = 'A nova palavra-passe deve ter pelo menos 8 caracteres.';
    }

    if (!formValues.repeatNewPassword) {
      errors.repeatNewPassword = 'Repita a nova palavra-passe.';
    } else if (formValues.newPassword !== formValues.repeatNewPassword) {
      errors.repeatNewPassword = 'As palavras-passe não coincidem.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage('');
    setMessageErr('');
    setSubtitleErr('');

    if (!validateForm() || isLoading) return;

    try {
      setIsLoading(true);
      const response = await fetch(`${URL}${newPasswordEndpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(formValues),
      });
      const responseData = await response.json();

      if (responseData.success) {
        setSuccessMessage(responseData.message);
        setFormValues({
          oldPassword: '',
          newPassword: '',
          repeatNewPassword: '',
        });
        setFieldErrors({});
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
      } else {
        setMessageErr(responseData.message || 'Não foi possível alterar a palavra-passe.');
        setSubtitleErr('Confirme a palavra-passe atual e os requisitos da nova palavra-passe.');
      }
    } catch (error) {
      setMessageErr('Não foi possível alterar a palavra-passe.');
      setSubtitleErr('Ocorreu um erro de comunicação. Tente novamente dentro de instantes.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormValues((previousValues) => ({
      ...previousValues,
      [name]: value,
    }));
    setFieldErrors((previousErrors) => ({
      ...previousErrors,
      [name]: undefined,
    }));
    setSuccessMessage('');
    setMessageErr('');
    setSubtitleErr('');
  };

  const fieldClass = (hasError) =>
    `block min-h-[48px] w-full rounded-xl border bg-white pl-11 pr-11 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
      hasError
        ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
        : 'border-gray-200 focus:border-green-cl focus:ring-emerald-100'
    }`;

  const passwordField = ({
    id,
    name,
    label,
    placeholder,
    autoComplete,
    visible,
    toggleVisible,
  }) => {
    const error = fieldErrors[name];

    return (
      <div>
        <label htmlFor={id} className="mb-2 block text-sm font-semibold text-gray-700">
          {label}
        </label>
        <div className="relative">
          <LockClosedIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            id={id}
            type={visible ? 'text' : 'password'}
            name={name}
            autoComplete={autoComplete}
            className={fieldClass(Boolean(error))}
            placeholder={placeholder}
            value={formValues[name]}
            onChange={handleChange}
            required
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
          />
          <button
            type="button"
            onClick={toggleVisible}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-green-cl focus:outline-none focus:ring-2 focus:ring-green-cl"
            aria-label={visible ? `Ocultar ${label.toLowerCase()}` : `Mostrar ${label.toLowerCase()}`}
          >
            {visible ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
          </button>
        </div>
        {error && (
          <p id={`${id}-error`} className="mt-1.5 text-xs font-medium text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-5 overflow-y-auto px-6 py-6 sm:px-8">
        {successMessage && (
          <SuccessMessage
            message={successMessage}
            subtitle="A nova palavra-passe já pode ser utilizada no próximo início de sessão."
            duration={4500}
          />
        )}
        {messageErr && (
          <ErrorMessage
            messageErr={messageErr}
            subtitleErr={subtitleErr}
            duration={5000}
          />
        )}

        {passwordField({
          id: 'current-password',
          name: 'oldPassword',
          label: 'Palavra-passe atual',
          placeholder: 'Introduza a palavra-passe atual',
          autoComplete: 'current-password',
          visible: showCurrentPassword,
          toggleVisible: () => setShowCurrentPassword((visible) => !visible),
        })}

        {passwordField({
          id: 'new-password',
          name: 'newPassword',
          label: 'Nova palavra-passe',
          placeholder: 'Introduza uma palavra-passe segura',
          autoComplete: 'new-password',
          visible: showNewPassword,
          toggleVisible: () => setShowNewPassword((visible) => !visible),
        })}

        {passwordField({
          id: 'confirm-password',
          name: 'repeatNewPassword',
          label: 'Confirmar nova palavra-passe',
          placeholder: 'Repita a nova palavra-passe',
          autoComplete: 'new-password',
          visible: showConfirmPassword,
          toggleVisible: () => setShowConfirmPassword((visible) => !visible),
        })}

        <PasswordStrengthIndicator password={formValues.newPassword} />
      </div>

      <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-100 bg-white px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="inline-flex min-h-[46px] items-center justify-center rounded-xl border border-gray-200 bg-white px-5 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-green-cl px-5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-green-eupago hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              A guardar...
            </>
          ) : (
            <>
              <LockClosedIcon className="h-4 w-4" />
              Guardar palavra-passe
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default Password;
